import {
  useApolloClient,
  useLazyQuery,
  useMutation,
  useSuspenseQuery,
} from "@apollo/client";
import {
  AnnotatedField,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
  EditPaymentIdentifiers,
  formInputToGqlMutationInput,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PrimaryFullScreenSpinner,
  SelectSeparator,
  toast,
  useAppForm,
} from "@repo/ui";
import {
  currencySchema,
  dateSchema,
  getCountryLists,
  prettyPrintEnum,
  setPaymentIdentifierSchema,
} from "@repo/utils";
import { useStore } from "@tanstack/react-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Suspense, useEffect, useState } from "react";
import { z } from "zod";
import { BusinessEntityCard } from "~/components/BusinessEntity/BusinessEntityCard";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { OrganisationCard } from "~/components/Organisation/OrganisationCard";
import { graphql } from "~/gql/client";
import { currencyValues } from "~/gql/client/enumValues";
import { opsHead } from "~/head";

const { commonCountriesList, uncommonCountriesList } = getCountryLists();

const demoAssetClasses = ["BANK_ACCOUNT", "FX"] as const;

const formSchema = z.object({
  organisationId: z.string().min(1),
  businessEntityId: z.string().min(1),
  assetClass: z.enum(demoAssetClasses),
  externalBankAccountDataInstitutionId: z.string().optional(),
  currency: currencySchema,
  country: z.string().min(1),
  nickname: z.string().min(1),
  openedAt: dateSchema,
  paymentIdentifiers: z.array(setPaymentIdentifierSchema),
});

type FormValues = z.input<typeof formSchema>;

export const Route = createFileRoute("/__auth/assets_/demo-new")({
  head: () => opsHead("New Demo Asset"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const client = useApolloClient();
  const navigate = useNavigate();

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminNewDemoExternalAccountForm {
        organisations {
          id
          name
          ...OrganisationCardFields
          businessEntities {
            id
            ...BusinessEntityCardFields
          }
        }
      }
    `),
  );

  const [
    searchInstitutions,
    { data: institutionSearchData, loading: institutionSearchLoading },
  ] = useLazyQuery(
    graphql(`
      query AdminNewDemoExternalInstitutionSearch($searchTerm: String) {
        externalBankAccountDataInstitutions(
          first: 20
          filter: { searchTerm: $searchTerm, active: true }
        ) {
          institutions {
            id
            displayName
            logoUrl
          }
        }
      }
    `),
  );

  const [createDemoAsset] = useMutation(
    graphql(`
      mutation AdminCreateDemoAsset($input: CreateDemoAssetInput!) {
        createDemoAsset(input: $input) {
          asset {
            id
            businessEntity {
              id
            }
          }
        }
      }
    `),
    {
      onError(error) {
        toast({
          kind: "error",
          autoHide: false,
          title: "Error creating demo asset",
          description:
            error.graphQLErrors?.[0]?.message ?? "Failed to create asset",
        });
      },
      onCompleted: (result) => {
        const { asset } = result.createDemoAsset;
        if (asset.businessEntity?.id) {
          client.cache.evict({
            id: `BusinessEntity:${asset.businessEntity.id}`,
            fieldName: "assets",
          });
        }
        void navigate({
          to: "/assets/$assetId",
          params: { assetId: asset.id },
        });
      },
    },
  );

  const form = useAppForm({
    defaultValues: {
      organisationId: "",
      businessEntityId: "",
      assetClass: "BANK_ACCOUNT",
      externalBankAccountDataInstitutionId: "",
      currency: "AUD",
      country: "",
      nickname: "",
      openedAt: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000),
      paymentIdentifiers: [],
    } as FormValues,
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: async ({ value }) => {
      const parsed = formSchema.safeParse(value);
      if (!parsed.success) {
        toast({ kind: "error", autoHide: false, title: "Error in form" });
        return;
      }
      if (
        parsed.data.assetClass === "BANK_ACCOUNT" &&
        !parsed.data.externalBankAccountDataInstitutionId
      ) {
        toast({
          kind: "error",
          autoHide: false,
          title: "Institution is required for external bank accounts",
        });
        return;
      }
      void createDemoAsset({
        variables: {
          input: {
            organisationId: parsed.data.organisationId,
            businessEntityId: parsed.data.businessEntityId,
            assetClass: parsed.data.assetClass,
            externalBankAccountDataInstitutionId:
              parsed.data.externalBankAccountDataInstitutionId ?? null,
            currency: parsed.data.currency,
            country: parsed.data.country,
            nickname: parsed.data.nickname,
            openedAt: parsed.data.openedAt,
            paymentIdentifiers: parsed.data.paymentIdentifiers.map(
              formInputToGqlMutationInput,
            ),
          },
        },
      });
    },
  });

  const selectedOrganisationId = useStore(
    form.store,
    (state) => state.values.organisationId,
  );
  const selectedOrganisation = data.organisations?.find(
    (o) => o.id === selectedOrganisationId,
  );

  const selectedAssetClass = useStore(
    form.store,
    (state) => state.values.assetClass,
  );

  const [institutionPickerOpen, setInstitutionPickerOpen] = useState(false);
  const [institutionSearch, setInstitutionSearch] = useState("");
  const [selectedInstitution, setSelectedInstitution] = useState<{
    id: string;
    displayName: string;
    logoUrl?: string | null;
  } | null>(null);

  const institutionResults =
    institutionSearchData?.externalBankAccountDataInstitutions?.institutions ??
    [];

  useEffect(() => {
    const businessEntities = selectedOrganisation?.businessEntities ?? [];
    if (businessEntities.length === 1) {
      form.setFieldValue("businessEntityId", businessEntities[0].id);
    }
  }, [form, selectedOrganisation]);

  return (
    <AdminPageContainer testId="admin-assets-new-demo-external">
      <AdminPageHeader
        title="New Demo Asset"
        subtitle="Manually create a demo asset for demonstration purposes"
      />
      <form.AppForm>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
          className="flex gap-6"
        >
          <div className="flex max-w-sm flex-1 flex-col gap-4">
            <form.AppField name="organisationId">
              {(field) => (
                <field.SelectField
                  label="Organisation"
                  placeholder="Select an organisation..."
                  onValueChange={(val) => {
                    field.handleChange(val);
                    field.form.setFieldValue("businessEntityId", "");
                  }}
                >
                  {data.organisations
                    ?.slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((o) => (
                      <field.SelectItem key={o.id} value={o.id}>
                        <OrganisationCard organisation={o} />
                      </field.SelectItem>
                    ))}
                </field.SelectField>
              )}
            </form.AppField>

            <form.AppField name="businessEntityId">
              {(field) => (
                <field.SelectField
                  label="Business Entity"
                  placeholder={
                    selectedOrganisation === undefined
                      ? "Select organisation first"
                      : "Select a business entity..."
                  }
                  disabled={selectedOrganisation === undefined}
                >
                  {selectedOrganisation?.businessEntities
                    ?.slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((be) => (
                      <field.SelectItem key={be.id} value={be.id}>
                        <BusinessEntityCard businessEntity={be} />
                      </field.SelectItem>
                    ))}
                </field.SelectField>
              )}
            </form.AppField>

            <form.AppField name="assetClass">
              {(field) => (
                <field.SelectField
                  label="Asset Class"
                  placeholder="Select asset class..."
                  onValueChange={(val) => {
                    field.handleChange(
                      val as (typeof demoAssetClasses)[number],
                    );
                    setSelectedInstitution(null);
                    form.setFieldValue(
                      "externalBankAccountDataInstitutionId",
                      "",
                    );
                    if (val === "FX") {
                      const currency = form.state.values.currency;
                      form.setFieldValue(
                        "nickname",
                        `Primary ${currency} Account`,
                      );
                    } else {
                      form.setFieldValue("nickname", "");
                    }
                  }}
                >
                  {demoAssetClasses.map((c) => (
                    <field.SelectItem key={c} value={c}>
                      {prettyPrintEnum(c)}
                    </field.SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>

            {selectedAssetClass === "BANK_ACCOUNT" && (
              <AnnotatedField label="Institution">
                <Popover
                  open={institutionPickerOpen}
                  onOpenChange={(o) => {
                    setInstitutionPickerOpen(o);
                    if (o) {
                      void searchInstitutions({
                        variables: { searchTerm: "" },
                      });
                    } else {
                      setInstitutionSearch("");
                    }
                  }}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-9 w-full justify-start gap-2 font-normal"
                    >
                      {selectedInstitution ? (
                        <>
                          {selectedInstitution.logoUrl && (
                            <img
                              src={selectedInstitution.logoUrl}
                              alt=""
                              className="size-4 rounded-sm object-contain"
                            />
                          )}
                          <span>{selectedInstitution.displayName}</span>
                        </>
                      ) : (
                        <span className="text-tertiary">
                          Search institutions...
                        </span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder="Search institutions..."
                        value={institutionSearch}
                        onValueChange={(v) => {
                          setInstitutionSearch(v);
                          void searchInstitutions({
                            variables: { searchTerm: v },
                          });
                        }}
                      />
                      <CommandList>
                        {institutionSearchLoading && <CommandLoading />}
                        <CommandEmpty>No institutions found.</CommandEmpty>
                        <CommandGroup>
                          {selectedInstitution && (
                            <CommandItem
                              value="__clear__"
                              onSelect={() => {
                                setSelectedInstitution(null);
                                form.setFieldValue(
                                  "externalBankAccountDataInstitutionId",
                                  "",
                                );
                                setInstitutionPickerOpen(false);
                              }}
                              className="text-danger"
                            >
                              Clear institution
                            </CommandItem>
                          )}
                          {institutionResults.map((institution) => (
                            <CommandItem
                              key={institution.id}
                              value={institution.id}
                              onSelect={() => {
                                setSelectedInstitution(institution);
                                form.setFieldValue(
                                  "externalBankAccountDataInstitutionId",
                                  institution.id,
                                );
                                form.setFieldValue(
                                  "nickname",
                                  `${institution.displayName} Transaction Account`,
                                );
                                setInstitutionPickerOpen(false);
                                setInstitutionSearch("");
                              }}
                              className="flex items-center gap-2"
                            >
                              {institution.logoUrl && (
                                <img
                                  src={institution.logoUrl}
                                  alt=""
                                  className="size-5 rounded-sm object-contain"
                                />
                              )}
                              <span>{institution.displayName}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </AnnotatedField>
            )}

            <form.AppField name="currency">
              {(field) => (
                <field.SelectField
                  label="Currency"
                  placeholder="Select currency..."
                  onValueChange={(val) => {
                    field.handleChange(val as (typeof currencyValues)[number]);
                    if (form.state.values.assetClass === "FX") {
                      form.setFieldValue("nickname", `Primary ${val} Account`);
                    }
                  }}
                >
                  {currencyValues.map((c) => (
                    <field.SelectItem key={c} value={c}>
                      {c}
                    </field.SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>

            <form.AppField name="country">
              {(field) => (
                <field.SelectField
                  label="Country"
                  placeholder="Select country..."
                >
                  {commonCountriesList.map(({ value, label }) => (
                    <field.SelectItem key={`common-${value}`} value={value}>
                      {label}
                    </field.SelectItem>
                  ))}
                  <SelectSeparator />
                  {uncommonCountriesList.map(({ value, label }) => (
                    <field.SelectItem key={`uncommon-${value}`} value={value}>
                      {label}
                    </field.SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>

            <form.AppField name="nickname">
              {(field) => <field.InputField label="Nickname" />}
            </form.AppField>

            <form.AppField name="openedAt">
              {(field) => <field.DatePickerField label="Opened At" />}
            </form.AppField>

            <div className="flex justify-end">
              <form.SubmitButton
                childrenWhileSubmitting="Creating..."
                disabled={form.state.isSubmitting}
              >
                Create
              </form.SubmitButton>
            </div>
          </div>

          <div className="flex flex-1 flex-col gap-4 rounded bg-secondary p-4">
            <EditPaymentIdentifiers
              form={form}
              fields={{ paymentIdentifiers: "paymentIdentifiers" }}
            />
          </div>
        </form>
      </form.AppForm>
    </AdminPageContainer>
  );
}
